import { FileText, Paperclip, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { ATTACHMENT_ACCEPT, DEFAULT_MAX_SIZE_MB } from "@/shared/utils/fileValidation";

import { MAX_INCIDENT_ATTACHMENTS } from "../constants/paymentIncidents.constants";
import { attachmentRejection, type IncidentAttachmentRejection } from "../utils/paymentIncidents";

export interface IncidentAttachmentsPickerProps {
  value: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  /** How many files fit; an incident entry takes five. */
  max?: number;
}

/**
 * Screenshots and documents for a timeline entry (or the proof of a wallet debit): pick several, see them,
 * drop one. A file that cannot go is named with the reason instead of vanishing.
 */
export function IncidentAttachmentsPicker({
  value,
  onChange,
  disabled,
  max = MAX_INCIDENT_ATTACHMENTS,
}: IncidentAttachmentsPickerProps) {
  const { t } = useTranslations("paymentIncidents");
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejected, setRejected] = useState<{ name: string; reason: IncidentAttachmentRejection } | null>(
    null,
  );
  const previews = useMemo(
    () => value.map((file) => (file.type.startsWith("image/") ? URL.createObjectURL(file) : null)),
    [value],
  );
  useEffect(() => () => previews.forEach((url) => url && URL.revokeObjectURL(url)), [previews]);

  const add = (files: File[]) => {
    const accepted: File[] = [];
    let refusal: { name: string; reason: IncidentAttachmentRejection } | null = null;
    for (const file of files) {
      if (value.length + accepted.length >= max) {
        refusal = { name: file.name, reason: "too_many" };
        break;
      }
      const reason = attachmentRejection(file);
      if (reason) refusal ??= { name: file.name, reason };
      else accepted.push(file);
    }
    setRejected(refusal);
    if (accepted.length) onChange([...value, ...accepted]);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="sr-only"
        disabled={disabled}
        onChange={(event) => add(Array.from(event.target.files ?? []))}
      />
      <div className="flex flex-wrap items-center gap-2">
        <ColocaButton
          type="button"
          variant="secondary"
          size="sm"
          icon={Paperclip}
          disabled={disabled || value.length >= max}
          onClick={() => inputRef.current?.click()}
        >
          {t("attachments_add")}
        </ColocaButton>
        <span className="text-xs text-muted-foreground">
          {t("attachments_hint", { max, size: DEFAULT_MAX_SIZE_MB })}
        </span>
      </div>
      {rejected && (
        <p role="alert" className="text-xs text-destructive">
          {t(`attachments_rejected_${rejected.reason}`, {
            name: rejected.name,
            max,
            size: DEFAULT_MAX_SIZE_MB,
          })}
        </p>
      )}
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {value.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${index}`}
              className="relative overflow-hidden rounded-md border border-border"
            >
              {previews[index] ? (
                <img src={previews[index] ?? undefined} alt={file.name} className="h-16 w-24 object-cover" />
              ) : (
                <span className="flex h-16 w-24 flex-col items-center justify-center gap-1 px-2 text-[11px] text-muted-foreground">
                  <FileText className="size-4" aria-hidden="true" />
                  <span className="w-full truncate text-center">{file.name}</span>
                </span>
              )}
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(value.filter((_, at) => at !== index))}
                aria-label={t("attachments_remove", { name: file.name })}
                className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm hover:bg-background focus-visible:outline-2 focus-visible:outline-ring"
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
